import React from 'react';
import { BellRing, LogOut, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BottomNav from '../../components/BottomNav';
import CustomerHeader from '../../components/CustomerHeader';
import PushNotificationCard from '../../components/PushNotificationCard';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';
import {
  fetchCurrentCustomerSession,
  logoutCustomerSession,
} from '../../utils/auth/customerSession';
import { clearCustomerAuthToken, saveCustomerAuthToken } from '../../utils/auth/customerAuthToken';
import SubpageHeader from '../Reserve/SubpageHeader';
import { useI18n } from '../../i18n';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [customerName, setCustomerName] = React.useState(localStorage.getItem('customerName') || 'Guest');
  const [customerId, setCustomerId] = React.useState(localStorage.getItem('customerId') || '');

  React.useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const session = await fetchCurrentCustomerSession();
        if (!active) return;

        const nextId = String(session.customer_id);
        localStorage.setItem('customerId', nextId);
        localStorage.setItem('customerName', session.name);
        saveCustomerAuthToken(session.auth_token);
        setCustomerId(nextId);
        setCustomerName(session.name);
      } catch (error) {
        if (!active) return;
        console.error('Failed to load current customer session:', error);
      }
    };

    void loadSession();

    return () => {
      active = false;
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await logoutCustomerSession();
    } catch (error) {
      console.error('Failed to logout customer session:', error);
      toast.error(t('toast.signOutFallback'));
    } finally {
      localStorage.removeItem('customerId');
      localStorage.removeItem('customerName');
      clearCustomerAuthToken();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-[#f3fbf9] via-white to-[#eef7fb]"
      style={{ ['--bottom-nav-space' as string]: '108px' }}
    >
      <CustomerHeader />

      <main
        className="flex-1 px-4 pt-5"
        style={{ paddingBottom: 'calc(var(--bottom-nav-space) + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-lg mx-auto space-y-4">
          <SubpageHeader title={t('settings.title')} onBack={() => navigate('/reserve')} />

          <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <div
              className="px-5 py-5 text-white"
              style={{
                background: `linear-gradient(135deg, ${BRAND_TIFFANY_BLUE}, #089d99)`,
              }}
            >
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
                  {t('account.member')}
                </div>
                <div className="mt-1 truncate text-[26px] font-semibold tracking-[-0.03em]">
                  {customerName}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 px-5 py-4">
              {customerId ? (
                <div className="rounded-full bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
                  ID {customerId}
                </div>
              ) : null}
              <div className="rounded-full bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                {t('settings.activeAccount')}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              <BellRing className="h-4 w-4 text-slate-400" />
              {t('settings.notifications')}
            </div>
            <PushNotificationCard />
          </section>

          <section className="rounded-[30px] border border-slate-200 bg-white/95 p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <div className="mb-3 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              <UserRound className="h-4 w-4 text-slate-400" />
              {t('settings.session')}
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex w-full items-center justify-between rounded-[24px] border border-rose-200 bg-[linear-gradient(135deg,rgba(255,241,242,1),rgba(255,251,251,1))] px-4 py-4 text-left text-rose-700 transition hover:bg-rose-50"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-rose-100 p-3">
                  <LogOut className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-lg font-semibold">{t('settings.signOut')}</div>
                  <div className="text-sm text-rose-500">{t('settings.signOutDetail')}</div>
                </div>
              </div>
            </button>
          </section>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default SettingsPage;
